/**
 * EstateGallery — the estate page's Google Places photos, fetched PER VIEW
 * from the browser after hydration (D14) and rendered through `PhotoMosaic`.
 *
 * Nothing Google reaches the server-rendered HTML: React Query never runs the
 * fetch during SSR, so the server (and the hydrating client) render the
 * loading skeleton, and the photos arrive only in the browser. That keeps
 * every Google serving URI out of crawler caches and shared caches, and keeps
 * the page's TTFB free of a Google round trip.
 *
 * And not even in the browser until the gallery comes into view: the fetch is
 * held by the app's one visibility latch (`useSeen`, the `MapBoundary`
 * primitive) within {@link ESTATE_GALLERY_ROOT_MARGIN} of the viewport. Every
 * fetch bills up to `ESTATE_PHOTOS_PER_VIEW` Google calls, so a hydrated page
 * whose gallery is never reached mints nothing. The skeleton stands in until
 * then — the same placeholder the fetch itself shows.
 *
 * Empty (the estate has no Google photos — most do not) and failed both
 * render NOTHING: a failed gallery is omitted, never an error panel, and the
 * BFF route and the backend already log and count the failure.
 */
import { useSeen } from '@/shared/hooks/useSeen';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { PublicId } from '@letsgethome/types/branded';
import { Skeleton } from '@letsgethome/ui/skeleton';
import { useState } from 'react';

import { PhotoMosaic } from './PhotoMosaic';
import { useEstatePhotos } from './use-estate-photos';

type Props = {
  estatePublicId: PublicId;
  /** Resolved estate name for alt text. */
  name: string;
};

/**
 * How near the viewport the gallery starts its fetch: a little ahead of the
 * fold, so a reader scrolling to it meets photos rather than a skeleton, and
 * far short of `MapBoundary`'s whole-viewport margin — a map load is free, a
 * gallery load is billed.
 */
export const ESTATE_GALLERY_ROOT_MARGIN = '200px 0px';

export function EstateGallery({ estatePublicId, name }: Props) {
  const t = useTranslations('estates.detail.gallery');
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const seen = useSeen(box, true, ESTATE_GALLERY_ROOT_MARGIN);
  const { data, isPending } = useEstatePhotos(estatePublicId, seen);
  if (isPending) {
    return (
      <div
        ref={setBox}
        className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8"
        role="status"
        aria-label={t('loading')}
        data-testid="estate-gallery-skeleton"
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Skeleton className="col-span-1 aspect-[4/3] rounded-xl sm:col-span-2 sm:aspect-auto sm:min-h-[380px]" />
          <div className="hidden grid-rows-2 gap-2 sm:grid">
            <Skeleton className="rounded-xl" />
            <Skeleton className="rounded-xl" />
          </div>
        </div>
      </div>
    );
  }
  return <PhotoMosaic photos={data ?? []} name={name} />;
}
